import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookmarkCheck, Copy, Trash2, Check } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';

export const SavedRephrasesCard = () => {
  const { t } = useLanguage();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await apiFetch('/api/translate/favorites');
    if (data?.favorites) setFavorites(data.favorites);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleCopy = async (fav) => {
    await navigator.clipboard.writeText(fav.rephrased);
    setCopiedId(fav.favorite_id);
    toast.success(t('copied'));
    setTimeout(() => setCopiedId(null), 1800);
  };

  const handleDelete = async (fav) => {
    if (!window.confirm(t('confirmDeleteRephrase'))) return;
    const { error } = await apiFetch(`/api/translate/favorites/${fav.favorite_id}`, {
      method: 'DELETE',
    });
    if (error) {
      toast.error('Failed to delete');
      return;
    }
    setFavorites(prev => prev.filter(f => f.favorite_id !== fav.favorite_id));
    toast.success(t('rephraseDeleted'));
  };

  return (
    <Card className="mb-6 mt-6" data-testid="saved-rephrases-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <BookmarkCheck className="h-5 w-5 text-[#00E5CC]" />
          {t('myRephrases')}
          {favorites.length > 0 && (
            <span className="text-sm text-muted-foreground font-normal">
              ({favorites.length})
            </span>
          )}
        </CardTitle>
        <p className="text-xs text-muted-foreground leading-relaxed mt-1">
          {/* Plain-English explainer so users know what this card is for —
              "Rephrases" alone was confusing. */}
          Phrases you've saved from the Thought Interpreter. Use them as
          ready-to-go scripts when you need the right words fast.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-xs text-muted-foreground">Loading...</p>
        ) : favorites.length === 0 ? (
          <p className="text-xs text-muted-foreground leading-relaxed">
            {t('noSavedRephrases')}
          </p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="saved-rephrases-list">
            {favorites.map(fav => (
              <div
                key={fav.favorite_id}
                className="p-3 rounded-lg border bg-muted/30 group"
                data-testid={`saved-rephrase-${fav.favorite_id}`}
              >
                <p className="text-xs text-muted-foreground truncate mb-1">
                  {fav.original}
                </p>
                <p className="text-sm font-medium text-foreground leading-relaxed">
                  {fav.rephrased}
                </p>
                <div className="flex items-center justify-between mt-2 gap-2">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {fav.tone}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(fav)}
                      className="h-7 px-2 text-xs"
                      data-testid={`copy-saved-${fav.favorite_id}`}
                    >
                      {copiedId === fav.favorite_id ? (
                        <Check className="h-3 w-3 mr-1" />
                      ) : (
                        <Copy className="h-3 w-3 mr-1" />
                      )}
                      {copiedId === fav.favorite_id ? t('copied') : t('copy')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(fav)}
                      className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                      data-testid={`delete-saved-${fav.favorite_id}`}
                      aria-label={t('deleteRephrase')}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SavedRephrasesCard;
